import { EstateDesignType } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import EstateMpProvider from "@/core/data-provider/data-mp/estate/estate-mp-provider.ts";
import useAppDispatch from "@/core/helpers/useAppDispatch.ts";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { IS_DEV } from "@/domains/main/constants/other";
import {
  estateSelector,
  selectedDesignVariantSelector,
  setPreviewedDesignVariant,
} from "@/domains/estate/slice";
import toast from "@/domains/toasts/helpers/toast";
import useLoadDesign from "@/domains/estate/hooks/useLoadDesign.ts";

export default () => {
  const { t } = useTranslation("estate");
  const [isBuying, setIsBuying] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const dispatch = useAppDispatch();
  const estate = useAppSelector(estateSelector);
  const selectedDesignVariant = useAppSelector(selectedDesignVariantSelector);
  const { loadDesign } = useLoadDesign();

  const buyHandler = useCallback(
    async (interiorPropId: number) => {
      if (IS_DEV) {
        setIsBuying(true);
        setTimeout(() => {
          setIsBuying(false);
        }, 1000);
      } else {
        try {
          setIsBuying(true);
          const response = await EstateMpProvider.buyDesignVariant(estate.id, interiorPropId);
          if (response instanceof RpcMpProviderSuccessResponse) {
            dispatch(setPreviewedDesignVariant(null));
            toast.success(
              t(`estate.design.${selectedDesignVariant?.type?.toLowerCase()}.purchased`),
            );
            await loadDesign();
          } else {
            console.error("payCurrRepairHandler error", response);
            toast.error(t("estate.design.errors.BUY_ERROR"));
          }
        } catch (e) {
          console.error(e);
          toast.error(t("estate.design.errors.BUY_ERROR_SYSTEM"));
        } finally {
          setIsBuying(false);
        }
      }
    },
    [selectedDesignVariant],
  );

  const previewHandler = useCallback(async () => {
    if (!selectedDesignVariant) {
      return;
    }

    if (IS_DEV) {
      setIsPreviewing(true);
      setTimeout(() => {
        setIsPreviewing(false);
      }, 1000);
    } else {
      try {
        setIsPreviewing(true);

        let wallInteriorPropId = -1;
        let furnitureInteriorPropId = -1;

        if (selectedDesignVariant.type === EstateDesignType.Wall) {
          wallInteriorPropId = selectedDesignVariant.interiorPropId;
        } else {
          furnitureInteriorPropId = selectedDesignVariant.interiorPropId;
        }

        const response = await EstateMpProvider.previewDesignVariant(
          wallInteriorPropId,
          furnitureInteriorPropId,
          estate.id,
          selectedDesignVariant.interiorPropId,
          selectedDesignVariant.name,
          selectedDesignVariant.type,
          selectedDesignVariant.price,
          estate.address,
        );

        if (response instanceof RpcMpProviderSuccessResponse) {
          dispatch(setPreviewedDesignVariant(selectedDesignVariant));
        } else {
          console.error("design variant preview error", response);
          toast.error(t("estate.design.errors.PREVIEW_ERROR"));
        }
      } catch (e) {
        console.error(e);
        toast.error(t("estate.design.errors.PREVIEW_ERROR_SYSTEM"));
      } finally {
        setIsPreviewing(false);
      }
    }
  }, [selectedDesignVariant]);

  const closePreviewHandler = useCallback(async () => {
    try {
      setIsPreviewing(true);
      const response = await EstateMpProvider.closePreviewDesignVariant();

      if (response instanceof RpcMpProviderSuccessResponse) {
        toast.success(t("estate.design.successClosePreview"));
        dispatch(setPreviewedDesignVariant(null));
      } else {
        console.log("close preview handler error", response);
        toast.error(t("estate.design.errors.CLOSE_PREVIEW_ERROR"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("estate.design.errors.CLOSE_PREVIEW_ERROR_SYSTEM"));
    } finally {
      setIsPreviewing(false);
    }
  }, []);

  return {
    buyHandler,
    previewHandler,
    closePreviewHandler,
    isPreviewing,
    isBuying,
  };
};
